import { type Variants, cubicBezier } from 'motion/react';
import * as motion from 'motion/react-client';

import { cn } from '@/lib/utils';

import { getWrapperMaxWidth } from '@/features/utils/utils';

import { MenuGroup, MenuGroupProps } from './MenuGroup';
import { MenuSheetCloseButton } from './MenuSheetCloseButton';

type MenuSheetProps = {
  menu: MenuGroupProps[];
  isOpen: boolean;
  onClose: () => void;
  onLinkClick?: (
    params: { title: string } | { url: string; keepMenuOpen?: boolean }
  ) => void;
};

export function MenuSheet({
  menu,
  isOpen,
  onClose,
  onLinkClick,
}: MenuSheetProps) {
  return (
    <motion.div
      className='headerLg:grid headerLg:grid-rows-[max-content] bg-base-dark-white headerLg:px-10 headerLg:max-h-[calc(100vh-50px)] absolute inset-0 top-full hidden h-max w-full origin-top overflow-hidden'
      initial='menuClosed'
      exit='menuClosed'
      animate={isOpen ? 'menuOpen' : 'menuClosed'}
      variants={menuVariants}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <motion.section
        className={cn(
          'relative mx-auto h-full w-full',
          getWrapperMaxWidth('mid')
        )}
        variants={menuInnerVariants}
        transition={{
          duration: 0.2,
          bounce: 0,
          stiffness: 50,
          ease: 'easeOut',
        }}
      >
        <ul className='headerLg:max-h-[calc(100vh-50px)] flex h-full'>
          {menu.map((group, i) => (
            <motion.li
              key={i}
              variants={menuGroupVariants}
              className={cn(
                group.lists.length === 0 && 'hidden',
                group.type === 'main' && 'headerLg:-ml-3 flex-grow',
                group.type === 'extra' &&
                  'headerLg:basis-[368px] headerLg:max-w-[368px]'
              )}
            >
              <MenuGroup
                type={group.type}
                carousel={group.carousel}
                lists={group.lists}
                onLinkClick={onLinkClick}
              />
            </motion.li>
          ))}
        </ul>
        <MenuSheetCloseButton onClose={onClose} />
      </motion.section>
    </motion.div>
  );
}

const menuVariants = {
  menuOpen: {
    clipPath: 'inset(0 0 0 0)',
    WebkitClipPath: 'inset(0 0 0 0)',
    visibility: 'visible',
    transition: {
      duration: 0.6,
      ease: cubicBezier(0, 1.04, 0, 1),
    },
  },
  menuClosed: {
    clipPath: 'inset(0 0 100% 0)',
    WebkitClipPath: 'inset(0 0 100% 0)',
    transition: {
      staggerDirection: -1,
    },
    transitionEnd: {
      visibility: 'hidden',
    },
  },
} satisfies Variants;

const menuInnerVariants = {
  menuOpen: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.4,
      ease: cubicBezier(0, 1.04, 0, 1),
    },
  },
  menuClosed: {
    opacity: 0.5,
    y: 100,
    transition: {
      staggerDirection: -1,
    },
  },
} satisfies Variants;

const menuGroupVariants = {
  menuOpen: {
    opacity: 1,

    transition: {
      duration: 0.6,
    },
  },
  menuClosed: {
    opacity: 0,
    transition: {
      duration: 0.6,
    },
  },
};
